import {Box, Collapse, Divider, IconButton, Typography, useMediaQuery, useTheme} from '@mui/material';
import {SectionType, SupportedDashboardActionTypes, WebsiteStructure} from '../../../api/models';
import {AddNewSectionDashboardPanelSectionItem} from './AddNewSectionDashboardPanelSectionItem';
import {buildStringFromSectionType} from '../../../common/utils';
import {canAddMoreThan3MediaSections, canCreateCatalogSection} from '../../../api/utils';
import {useContext, useEffect, useState} from 'react';
import {AuthContext} from '../../../api/context';
import {DashboardActionItem} from './DashboardActionItem';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const actions: SupportedDashboardActionTypes[] = ['share', 'delete'];

const DashboardPanelSectionItem = ({
    title,
    sectionId,
    activeSection,
    updateActiveSection,
}: {
    title: string;
    sectionId: string;
    activeSection?: string;
    updateActiveSection: (newSectionType: string) => void;
}) => {
    const isSectionActive = sectionId === activeSection;
    return (
        <div
            onClick={() => {
                updateActiveSection(sectionId);
            }}
            style={{
                paddingTop: 6,
                cursor: 'pointer',
                textDecorationLine: isSectionActive ? 'underline' : 'none',
            }}>
            <Box>
                <Typography
                    style={{
                        fontWeight: 400,
                        color: 'black',
                    }}
                    variant='body1'
                    noWrap
                    component='div'>
                    {title}
                </Typography>
            </Box>
        </div>
    );
};
export const DashboardPanel = ({
    websiteStructure,
    activeSection,
    updateActiveSection,
    refetch,
    throwSnackbarMessage,
}: {
    websiteStructure: WebsiteStructure;
    activeSection?: string;
    updateActiveSection: (newSectionType: string) => void;
    refetch: VoidFunction;
    throwSnackbarMessage: (message: string) => void;
}) => {
    const [supportedActions, setSupportedActions] = useState<SupportedDashboardActionTypes[]>(actions);
    const context = useContext(AuthContext);
    const theme = useTheme();
    const [openActions, setOpenActions] = useState(false);
    const [openSections, setOpenSections] = useState(false);
    const sectionDetails = websiteStructure.sections.map((e) => {
        return {
            sectionId: e.id,
            sectionType: e.sectionType,
        };
    });
    const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const currentNumberOfMediaSections = websiteStructure.sections.filter((e) => e.sectionType === SectionType.MEDIA)
        .length;
    const allSections = Object.keys(SectionType)
        .map((e) => e as SectionType)
        .filter((el) => el !== SectionType.CATALOG)
        .filter((el) => el !== SectionType.MEDIA);
    const sectionTypes = sectionDetails.map((e) => e.sectionType);
    const missingSections: SectionType[] = allSections.filter((e) => sectionTypes.indexOf(e) === -1);
    if (canAddMoreThan3MediaSections(context?.userDetails, currentNumberOfMediaSections)) {
        missingSections.push(SectionType.MEDIA);
    }
    if (canCreateCatalogSection(context?.userDetails)) {
        missingSections.push(SectionType.CATALOG);
    }

    useEffect(() => {
        if (canCreateCatalogSection(context?.userDetails)) {
            supportedActions.push('orders-dashboard');
            supportedActions.push('payments-dashboard');
            setSupportedActions(supportedActions);
        }
    }, []);

    const handleToggleForActions = () => {
        setOpenActions((prevOpen) => !prevOpen);
    };

    const handleToggleForSections = () => {
        setOpenSections((prevOpen) => !prevOpen);
    };

    return (
        <div
            style={{
                borderTopRightRadius: 8,
                borderBottomRightRadius: 8,
                boxShadow: '2px 2px 12px 4px rgba(200, 200, 200, 0.8)',
                marginRight: 4,
                paddingTop: isMobileScreen ? 5 : 20,
                paddingBottom: 20,
                paddingLeft: 16,
            }}>
            <Box>
                <Typography
                    style={{
                        fontWeight: 700,
                        color: 'black',
                    }}
                    variant='h6'
                    noWrap
                    component='div'>
                    Actions
                    {isMobileScreen && (
                        <IconButton onClick={handleToggleForActions}>
                            <ExpandMoreIcon />
                        </IconButton>
                    )}
                </Typography>
            </Box>
            {isMobileScreen ? (
                <Collapse in={openActions}>
                    {supportedActions.map((action) => {
                        return <DashboardActionItem websiteStructure={websiteStructure} actionType={action} />;
                    })}
                </Collapse>
            ) : (
                supportedActions.map((action) => {
                    return <DashboardActionItem websiteStructure={websiteStructure} actionType={action} />;
                })
            )}
            <Box>
                <Typography
                    style={{
                        fontWeight: 700,
                        color: 'black',
                    }}
                    variant='h6'
                    noWrap
                    component='div'>
                    Sections
                    {isMobileScreen && (
                        <IconButton onClick={handleToggleForSections}>
                            <ExpandMoreIcon />
                        </IconButton>
                    )}
                </Typography>
            </Box>
            {isMobileScreen ? (
                <Collapse in={openSections}>
                    {sectionDetails.map((detail) => {
                        return (
                            <DashboardPanelSectionItem
                                activeSection={activeSection}
                                updateActiveSection={updateActiveSection}
                                sectionId={detail.sectionId}
                                title={buildStringFromSectionType(detail.sectionType)}
                            />
                        );
                    })}
                </Collapse>
            ) : (
                sectionDetails.map((detail) => {
                    return (
                        <DashboardPanelSectionItem
                            activeSection={activeSection}
                            updateActiveSection={updateActiveSection}
                            sectionId={detail.sectionId}
                            title={buildStringFromSectionType(detail.sectionType)}
                        />
                    );
                })
            )}
            <Divider
                style={{
                    marginTop: 20,
                    marginBottom: 20,
                }}
            />
            <AddNewSectionDashboardPanelSectionItem
                throwSnackbarMessage={throwSnackbarMessage}
                missingSections={missingSections}
                refetch={refetch}
            />
            <Divider
                style={{
                    marginTop: 20,
                }}
            />
        </div>
    );
};
